import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  Box,
} from '@mui/material';
import { VehiclePictureTab, TAB_VISIBILITY_OPTIONS } from 'types/vehicles/types';

interface TabManagementDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  existingTab?: VehiclePictureTab;
  onClose: () => void;
  onSubmit: (name: string, visibility: string[]) => void;
}

export const TabManagementDialog: React.FC<TabManagementDialogProps> = ({
  open,
  mode,
  existingTab,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState<string[]>([]);
  const [errors, setErrors] = useState<{ name?: string; visibility?: string }>({});

  // Initialize form fields when dialog opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && existingTab) {
        setName(existingTab.name);
        setVisibility(existingTab.visibility);
      } else {
        setName('');
        setVisibility([]);
      }
      setErrors({});
    }
  }, [open, mode, existingTab]);

  const handleVisibilityChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setVisibility(typeof value === 'string' ? value.split(',') : value);
  };

  const validate = (): boolean => {
    const newErrors: { name?: string; visibility?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(name, visibility);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Create New Tab' : 'Edit Tab'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
          />

          <FormControl fullWidth error={!!errors.visibility}>
            <InputLabel id="visibility-label">Visibility (leave empty for Admin only)</InputLabel>
            <Select
              labelId="visibility-label"
              multiple
              value={visibility}
              onChange={handleVisibilityChange}
              input={<OutlinedInput label="Visibility (leave empty for Admin only)" />}
              renderValue={selected => {
                if (selected.length === 0) {
                  return 'Admin only';
                }
                const selectedLabels = TAB_VISIBILITY_OPTIONS.filter(option => selected.includes(option.value)).map(
                  option => option.label
                );
                return selectedLabels.join(', ');
              }}
            >
              {TAB_VISIBILITY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={visibility.indexOf(option.value) > -1} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
            {errors.visibility ? (
              <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.75 }}>{errors.visibility}</Box>
            ) : null}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {mode === 'create' ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
